import {useEffect, useState} from 'react'
import {fbsdb, fbsstorage} from '../../utils/firebase'
import {doc, getDoc, setDoc} from 'firebase/firestore'
import {ref, uploadBytes, getDownloadURL, deleteObject} from 'firebase/storage'
import {Alert} from 'react-bootstrap'

const appConfigDB = 'app_config'
const configSetting = 'setting'
const posterField = 'poster_url'
const storageFolder = 'posters'
const POSTER_SLOTS = 3
const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB

// The mobile app's home slider reads `poster_url` from app_config/setting as an
// ordered array of image URLs and shows one slide per URL. We keep that array
// compacted (no empty gaps); the 3 fixed slots below map to array positions.
const PosterManagement: React.FC = () => {
  const [slots, setSlots] = useState<(string | null)[]>(Array(POSTER_SLOTS).fill(null))
  const [busySlot, setBusySlot] = useState<number | null>(null)
  const [initializing, setInitializing] = useState(true)
  const [alert, setAlert] = useState({isAlert: false, msg: '', variant: 'primary'})

  const configRef = doc(fbsdb, appConfigDB, configSetting)

  const showAlert = (msg: string, variant: string) => setAlert({isAlert: true, msg, variant})
  const clearAlert = () => setAlert({isAlert: false, msg: '', variant: 'primary'})

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(configRef)
        const urls: string[] = (snap.data()?.[posterField] as string[]) ?? []
        const next: (string | null)[] = Array(POSTER_SLOTS).fill(null)
        urls.slice(0, POSTER_SLOTS).forEach((url, i) => (next[i] = url))
        setSlots(next)
      } catch (e) {
        console.log(e)
        showAlert('Failed to load posters.', 'danger')
      } finally {
        setInitializing(false)
      }
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Persist the compacted array (skip empty slots) to Firestore.
  const persist = async (next: (string | null)[]) => {
    const poster_url = next.filter((url): url is string => !!url)
    await setDoc(configRef, {[posterField]: poster_url}, {merge: true})
  }

  const handleFile = async (index: number, file: File) => {
    clearAlert()
    if (!file.type.startsWith('image/')) {
      showAlert('Please choose an image file.', 'danger')
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      showAlert('Image must be 5MB or smaller.', 'danger')
      return
    }

    setBusySlot(index)
    try {
      const previous = slots[index]
      const ext = file.name.split('.').pop() || 'jpg'
      const path = `${storageFolder}/poster_${index}_${Date.now()}.${ext}`
      const storageRef = ref(fbsstorage, path)
      await uploadBytes(storageRef, file)
      const url = await getDownloadURL(storageRef)

      const next = [...slots]
      next[index] = url
      setSlots(next)
      await persist(next)

      // Best-effort cleanup of the image we just replaced.
      if (previous) {
        try {
          await deleteObject(ref(fbsstorage, previous))
        } catch (e) {
          console.log('Old poster cleanup failed', e)
        }
      }
      showAlert(`Poster ${index + 1} saved.`, 'success')
    } catch (e) {
      console.log(e)
      showAlert(`Failed to upload poster ${index + 1}.`, 'danger')
    } finally {
      setBusySlot(null)
    }
  }

  const handleDelete = async (index: number) => {
    const current = slots[index]
    if (!current) return
    clearAlert()
    setBusySlot(index)
    try {
      const next = [...slots]
      next[index] = null
      setSlots(next)
      await persist(next)

      try {
        await deleteObject(ref(fbsstorage, current))
      } catch (e) {
        console.log('Poster file cleanup failed', e)
      }
      showAlert(`Poster ${index + 1} removed.`, 'success')
    } catch (e) {
      console.log(e)
      showAlert(`Failed to remove poster ${index + 1}.`, 'danger')
    } finally {
      setBusySlot(null)
    }
  }

  return (
    <>
      {alert.isAlert && (
        <Alert key={alert.variant} variant={alert.variant}>
          {alert.msg}
        </Alert>
      )}
      <div className='card mb-5 mb-xl-10'>
        <div className='card-header border-0'>
          <div className='card-title m-0'>
            <h3 className='fw-bolder m-0'>App Poster Management</h3>
          </div>
        </div>

        <div className='card-body border-top p-9'>
          <p className='text-muted fs-6 mb-8'>
            These posters appear in the driver app home screen slider. You can manage up to{' '}
            {POSTER_SLOTS} posters. The slider shows one slide per uploaded poster.
          </p>

          {initializing ? (
            <div className='d-flex justify-content-center py-10'>
              <span className='spinner-border align-middle'></span>
            </div>
          ) : (
            <div className='row g-8'>
              {slots.map((url, index) => {
                const busy = busySlot === index
                return (
                  <div className='col-md-4' key={index}>
                    <div className='card card-bordered h-100'>
                      <div className='card-header border-0 pt-5'>
                        <h4 className='card-title fw-bold'>Poster {index + 1}</h4>
                      </div>
                      <div className='card-body pt-2'>
                        <div
                          className='d-flex align-items-center justify-content-center bg-light rounded mb-5 overflow-hidden'
                          style={{height: 180}}
                        >
                          {url ? (
                            <img
                              src={url}
                              alt={`Poster ${index + 1}`}
                              style={{width: '100%', height: '100%', objectFit: 'cover'}}
                            />
                          ) : (
                            <span className='text-muted'>No image</span>
                          )}
                        </div>

                        <div className='d-flex gap-3'>
                          <label
                            className={`btn btn-sm btn-light-primary flex-grow-1 ${
                              busy ? 'disabled' : ''
                            }`}
                          >
                            {busy ? (
                              <span className='indicator-progress' style={{display: 'block'}}>
                                Please wait...{' '}
                                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                              </span>
                            ) : url ? (
                              'Replace'
                            ) : (
                              'Upload'
                            )}
                            <input
                              type='file'
                              accept='image/*'
                              hidden
                              disabled={busy}
                              onChange={(e) => {
                                const file = e.target.files?.[0]
                                if (file) handleFile(index, file)
                                e.target.value = ''
                              }}
                            />
                          </label>
                          {url && (
                            <button
                              type='button'
                              className='btn btn-sm btn-light-danger'
                              disabled={busy}
                              onClick={() => handleDelete(index)}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default PosterManagement
